import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import MaterialIcon from "../elements/MaterialIcon.jsx";
import SearchInput from "../elements/SearchInput.jsx";
import { loadMealIdeas } from "../../redux/actions/mealIdeas.js";
import { DEFAULT_BOUGHT_PAGE, fetchBoughtLists, foodOpsDrained } from "../../redux/actions/food.js";
import { searchBoughtLists, selectBoughtLists, selectMealIdeasLoaded, selectMealIdeasLoading } from "../../redux/food/selectors.js";
import BoughtListItems, { useBoughtListItemActions } from "./calendar/BoughtListItems.jsx";

/** The oldest day among a page's lists, where the next page starts. */
const oldestDay = (page) => (page.grocery_lists || [])
	.map((list) => list.purchased_on)
	.filter(Boolean)
	.sort()[0] || null;

/**
 * The shopping history, opened from the Groceries heading: every bought list, newest first under
 * month headings, as the record of what was bought and when.
 *
 * It shows the bought lists the food store holds, and pages in older ones through food/bought_lists,
 * a page at a time from the newest, for the search typed or for none. The server's `totals` for that
 * search are the summary; until they arrive (or offline) the summary counts what the store holds.
 * Putting an item back asks for them again, since it can take an item, and a trip, out of them.
 * A search waits `searchDelay` ms after the last key before it asks.
 */
export default function GroceryListArchive({ searchDelay = 250 }) {
	const dispatch = useDispatch();
	const lists = useSelector((state) => selectBoughtLists(state.food.entities));
	const mealIdeasLoaded = useSelector(selectMealIdeasLoaded);
	const mealIdeasLoading = useSelector(selectMealIdeasLoading);
	const itemActions = useBoughtListItemActions();
	const [query, setQuery] = useState('');
	// What the server said for `query`: where its next page starts, whether there is one, the totals.
	const [paging, setPaging] = useState({ query: null, before: null, hasMore: false, totals: null });
	const [loading, setLoading] = useState(false);
	// The search the latest request was for, so an answer for an earlier one is dropped.
	const asking = useRef(null);
	const mounted = useRef(true);

	// Items made from a meal idea link to it by name, which only the meal ideas store knows.
	useEffect(() => {
		if(!mealIdeasLoaded && !mealIdeasLoading) {
			dispatch(loadMealIdeas());
		}
		return () => {
			mounted.current = false;
		};
	}, []);

	const ask = (search, before) => {
		asking.current = search;
		setLoading(true);
		return dispatch(fetchBoughtLists({ query: search, before, limit: DEFAULT_BOUGHT_PAGE })).then((page) => {
			if(!mounted.current || asking.current !== search) {
				return;
			}
			setLoading(false);
			if(!page) {
				return;
			}
			setPaging((previous) => ({
				query: search,
				before: oldestDay(page) || (previous.query === search ? previous.before : null),
				hasMore: !!page.has_more,
				totals: page.totals || null,
			}));
		});
	};

	// The first page for each search, once typing stops.
	useEffect(() => {
		const search = query.trim();
		const timer = setTimeout(() => ask(search, null), search ? searchDelay : 0);
		return () => clearTimeout(timer);
	}, [query.trim()]);

	// The totals alone, again, for the search they were for: a one-list page keeps the paging as it is.
	const askTotals = (search) => dispatch(fetchBoughtLists({ query: search, limit: 1 })).then((page) => {
		if(!mounted.current || !page || !page.totals) {
			return;
		}
		setPaging((previous) => (previous.query === search ? { ...previous, totals: page.totals } : previous));
	});
	// Once the put-back has reached the server: until then its totals still count the item. Offline it
	// cannot, and the totals wait for the next search.
	const onPutBack = (item) => {
		const search = query.trim();
		return Promise.resolve(itemActions.onPutBack(item))
			.then(() => dispatch(foodOpsDrained()))
			.then(() => askTotals(search), () => {});
	};

	const answered = paging.query === query.trim();
	return (
		<GroceryListHistory
			lists={lists}
			query={query}
			onQueryChange={setQuery}
			totals={answered ? paging.totals : null}
			hasMore={answered && paging.hasMore}
			loading={loading}
			onMore={() => ask(query.trim(), paging.before)}
			{...itemActions}
			onPutBack={onPutBack}
		/>
	);
}

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Today and yesterday by name, any other day by weekday and date; the month heading has the year. The
 * server names a bought list after its day, so only a name that is something else is shown.
 */
function tripName(list, today) {
	const day = moment(list.purchased_on, 'YYYY-MM-DD');
	if(list.name && list.name !== day.format('LL')) {
		return list.name;
	}
	if(day.isSame(today, 'day')) {
		return 'Today';
	}
	if(day.isSame(today.clone().subtract(1, 'day'), 'day')) {
		return 'Yesterday';
	}
	return day.format('dddd, MMMM D');
}

const namedRows = (list) => (list.rows || []).filter((row) => row.name !== '');

/**
 * Bought lists (selectBoughtLists' shape: each with its `rows`) as trips grouped by month. A trip
 * opens in place to show what was bought, and any item can be put back on the active list from
 * there. Searching keeps only the trips that bought a matching item, opened to just those items,
 * which answers "when did I last buy this".
 *
 * `totals` ({ trips, items }) are the summary when given, else what `lists` holds is counted. With
 * `hasMore`, a button under the trips asks `onMore` for older ones. The search is the owner's when it
 * passes `query` and `onQueryChange`, and kept here otherwise.
 */
export function GroceryListHistory({
	                                   lists,
	                                   mealIdeasById = {},
	                                   onPutBack,
	                                   today = moment(),
	                                   query: ownedQuery,
	                                   onQueryChange = null,
	                                   totals = null,
	                                   hasMore = false,
	                                   loading = false,
	                                   onMore = null,
                                   }) {
	const [keptQuery, setKeptQuery] = useState('');
	const query = ownedQuery === undefined ? keptQuery : ownedQuery;
	const setQuery = onQueryChange || setKeptQuery;
	const [openKeys, setOpenKeys] = useState(() => new Set());

	const bought = lists.filter((list) => !!list.purchased_on);
	const months = searchBoughtLists(bought, query);
	const needle = query.trim().toLowerCase();
	const searching = needle !== '';
	const matching = months.reduce((count, [, trips]) => count + trips.length, 0);
	const summary = totals || {
		trips: searching ? matching : bought.length,
		items: months.reduce((total, [, trips]) => total + trips.reduce((count, list) => count + namedRows(list)
			.filter((row) => !searching || row.name.toLowerCase().includes(needle)).length, 0), 0),
	};

	const toggle = (key) => {
		setOpenKeys((previous) => {
			const next = new Set(previous);
			next.has(key) ? next.delete(key) : next.add(key);
			return next;
		});
	};

	if(!bought.length && !searching && !(totals && totals.trips)) {
		return (
			<div className='grocery_history grocery_history--empty'>
				<MaterialIcon className='grocery_history__empty-icon'>shopping_basket</MaterialIcon>
				<h5 className='grocery_history__title'>Nothing bought yet</h5>
				<p className='grocery_history__hint'>
					Tick items off the grocery list and they land here, under the day you bought them.
				</p>
			</div>
		);
	}

	const renderTrip = (list) => {
		const open = searching || openKeys.has(list.key);
		const items = namedRows(list);
		const day = moment(list.purchased_on, 'YYYY-MM-DD');
		return (
			<div
				key={list.key}
				className={`grocery_history__trip${open ? ' is-open' : ''}`}
				data-id={list.id}
			>
				<button
					type='button'
					className='btn-flat grocery_history__trip-toggle'
					aria-expanded={open}
					onClick={() => toggle(list.key)}
					disabled={searching}
				>
					<span className='grocery_history__date' aria-hidden='true'>
						<span className='grocery_history__date-weekday'>{day.format('ddd')}</span>
						<span className='grocery_history__date-day'>{day.format('D')}</span>
					</span>
					<span className='grocery_history__trip-text'>
						<span className='grocery_history__trip-name'>{tripName(list, today)}</span>
						{!open &&
						<span className='grocery_history__preview'>
							{items.map((item) => item.name).join(', ')}
						</span>
						}
					</span>
					<span className='grocery_history__count' title={plural(items.length, 'item')}>{items.length}</span>
					{!searching &&
					<MaterialIcon className='grocery_history__chevron'>expand_more</MaterialIcon>
					}
				</button>
				{open &&
				<BoughtListItems
					items={searching ? items.filter((item) => item.name.toLowerCase().includes(needle)) : items}
					query={query}
					mealIdeasById={mealIdeasById}
					onPutBack={onPutBack}
				/>
				}
			</div>
		);
	};

	return (
		<div className='grocery_history'>
			<div className='grocery_history__header'>
				<div>
					<h5 className='grocery_history__title'>Shopping history</h5>
					<div className='grocery_history__summary'>
						{plural(summary.trips, 'trip')} · {plural(summary.items, 'item')}
					</div>
				</div>
				<SearchInput
					className='grocery_history__search'
					placeholder='Find an item'
					value={query}
					onChange={setQuery}
				/>
			</div>
			{!months.length && !loading && !hasMore &&
			<p className='grocery_history__hint'>Nothing bought matches “{query.trim()}”.</p>
			}
			{months.map(([month, trips]) => (
				<section key={month} className='grocery_history__month'>
					<h6 className='grocery_history__month-name'>
						{month}
						<span className='grocery_history__month-count'>{plural(trips.length, 'trip')}</span>
					</h6>
					<div className='grocery_history__card'>
						{trips.map(renderTrip)}
					</div>
				</section>
			))}
			{hasMore && !!onMore &&
			<button
				type='button'
				className='btn-flat grocery_history__more'
				disabled={loading}
				onClick={onMore}
			>
				{loading ? 'Loading…' : 'Older trips'}
			</button>
			}
		</div>
	);
}
